import { useEffect, useState, useCallback } from "react";

const SOFT_MESSAGES = [
  "Your hive may be ready for a gentle reset.",
  "Bedtime can begin softly whenever your family is ready.",
  "A calm moment is waiting if you need it.",
];

const todayKey = () => new Date().toISOString().slice(0, 10);
const dayCode = () => ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];

const minutesNow = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};
const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/**
 * Gentle reminder hook — entirely OPT-IN, parent-controlled.
 * - Default OFF.
 * - Honors quiet_days.
 * - Fires AT MOST once per day (localStorage tracked).
 * - Returns { activeMessage, dismiss, isOpen }.
 *
 * NOTE: This shows an in-app soft banner only. Browser notifications fire only
 * if the parent explicitly enables them AND grants OS permission.
 */
export const useGentleReminders = (settings) => {
  const [activeMessage, setActiveMessage] = useState(null);

  const fire = useCallback(() => {
    const key = `bn_reminder_fired_${todayKey()}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    const msg = SOFT_MESSAGES[Math.floor(Math.random() * SOFT_MESSAGES.length)];
    setActiveMessage(msg);
    if (settings?.browser_push_enabled && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("BeeNest", {
          body: msg,
          silent: true,
          tag: "beenest-bedtime",
        });
      } catch {}
    }
  }, [settings]);

  useEffect(() => {
    if (!settings?.reminders_enabled || !settings.reminder_time) return;
    if ((settings.quiet_days || []).includes(dayCode())) return;

    const target = toMinutes(settings.reminder_time);
    if (target === null) return;
    const now = minutesNow();
    const todayKeyVal = `bn_reminder_fired_${todayKey()}`;

    // If we're already past the reminder time today and haven't fired yet, fire softly
    if (now >= target && now < target + 30 && !localStorage.getItem(todayKeyVal)) {
      const t = setTimeout(fire, 1500);
      return () => clearTimeout(t);
    }

    // Otherwise schedule one fire when target time arrives (only while session open)
    if (now < target) {
      const ms = (target - now) * 60 * 1000;
      const t = setTimeout(fire, ms);
      return () => clearTimeout(t);
    }
  }, [settings, fire]);

  return {
    activeMessage,
    dismiss: () => setActiveMessage(null),
    isOpen: !!activeMessage,
  };
};

export const requestBrowserNotificationPermission = async () => {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const r = await Notification.requestPermission();
  return r === "granted";
};

export default useGentleReminders;
